import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Activity, Download, FileText, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";

async function logExportEvent(
  surface: string,
  format: "csv" | "pdf" | "json",
  filters: Record<string, unknown>,
  rowCount: number | null,
  mode: "sync" | "async",
  jobId?: string,
) {
  try {
    const { data: me } = await supabase.auth.getUser();
    if (!me.user) return;
    await supabase.from("admin_export_events").insert({
      user_id: me.user.id,
      surface,
      format,
      mode,
      filters: filters as never,
      row_count: rowCount,
      job_id: jobId ?? null,
      user_agent: navigator.userAgent.slice(0, 200),
    });
  } catch { /* non-fatal */ }
}

function toCsv(rows: AuditRow[]): string {
  const cols = ["created_at","tool_name","outcome","latency_ms","user_id","ip_hash","ticket_id","request_id","conversation_id","error"];
  const esc = (v: unknown) => {
    if (v == null) return "";
    const s = typeof v === "string" ? v : JSON.stringify(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [cols.join(","), ...rows.map(r => cols.map(c => esc((r as any)[c])).join(","))].join("\n");
}

function download(name: string, content: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function openPdfPrint(rows: AuditRow[], meta: { tool: string; outcome: string }) {
  const html = `<!doctype html><html><head><meta charset="utf-8"/><title>Helpdesk audit export</title>
<style>
body{font:12px -apple-system,Segoe UI,Roboto,sans-serif;margin:24px;color:#111}
h1{font-size:16px;margin:0 0 4px}
.meta{color:#555;margin-bottom:12px}
table{width:100%;border-collapse:collapse;font-size:10px}
th,td{border:1px solid #ddd;padding:4px 6px;text-align:left;vertical-align:top}
th{background:#f4f4f5}
.badge{display:inline-block;padding:1px 6px;border-radius:4px;background:#eee;font-size:9px}
.err{color:#b91c1c}
@media print{@page{size:landscape;margin:12mm}}
</style></head><body>
<h1>Helpdesk audit export</h1>
<div class="meta">Generated ${new Date().toLocaleString()} · tool=${meta.tool} · outcome=${meta.outcome} · rows=${rows.length}</div>
<table><thead><tr>
<th>When</th><th>Tool</th><th>Outcome</th><th>Latency</th><th>User/IP</th><th>Ticket</th><th>Request</th><th>Error</th>
</tr></thead><tbody>
${rows.map(r => `<tr>
<td>${new Date(r.created_at).toISOString()}</td>
<td>${r.tool_name}</td>
<td><span class="badge">${r.outcome}</span></td>
<td>${r.latency_ms ?? ""}</td>
<td>${r.user_id ? r.user_id.slice(0,8) : "ip:"+(r.ip_hash ?? "").slice(0,8)}</td>
<td>${r.ticket_id ? r.ticket_id.slice(0,8) : ""}</td>
<td>${r.request_id ?? ""}</td>
<td class="err">${r.error ? String(r.error).replace(/[<>]/g,"") : ""}</td>
</tr>`).join("")}
</tbody></table>
<script>window.addEventListener('load',()=>setTimeout(()=>window.print(),200));</script>
</body></html>`;
  const w = window.open("", "_blank");
  if (!w) return toast.error("Popup blocked — allow popups to export PDF");
  w.document.open(); w.document.write(html); w.document.close();
}


type AuditRow = {
  id: string;
  request_id: string | null;
  conversation_id: string | null;
  user_id: string | null;
  ip_hash: string | null;
  tool_name: string;
  outcome: "ok" | "error" | "denied" | "rate_limited";
  latency_ms: number | null;
  input_summary: Record<string, unknown> | null;
  output_summary: Record<string, unknown> | null;
  ticket_id: string | null;
  error: string | null;
  created_at: string;
};

const TOOLS = ["all", "chat", "kb_search", "create_ticket", "get_license_status", "open_billing_portal"] as const;
const OUTCOMES = ["all", "ok", "error", "denied", "rate_limited"] as const;

const outcomeVariant = (o: AuditRow["outcome"]): "default" | "secondary" | "destructive" | "outline" => {
  if (o === "ok") return "secondary";
  if (o === "denied" || o === "rate_limited") return "outline";
  return "destructive";
};

type ExportJob = {
  id: string;
  format: "csv" | "pdf";
  status: "queued" | "running" | "ready" | "failed" | "canceled";
  progress: number;
  row_count: number | null;
  storage_path: string | null;
  error: string | null;
  created_at: string;
};

export function HelpdeskAuditPanel() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [tool, setTool] = useState<(typeof TOOLS)[number]>("all");
  const [outcome, setOutcome] = useState<(typeof OUTCOMES)[number]>("all");
  const [jobs, setJobs] = useState<ExportJob[]>([]);
  const [starting, setStarting] = useState(false);

  const loadJobs = async () => {
    const { data } = await supabase
      .from("helpdesk_export_jobs")
      .select("id,format,status,progress,row_count,storage_path,error,created_at")
      .order("created_at", { ascending: false })
      .limit(5);
    setJobs((data as ExportJob[]) ?? []);
  };

  useEffect(() => {
    void loadJobs();
    const ch = supabase
      .channel(`helpdesk_export_jobs:${Math.random().toString(36).slice(2)}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "helpdesk_export_jobs" }, (payload) => {
        setJobs((prev) => {
          const next = [...prev];
          const row = payload.new as ExportJob;
          const idx = next.findIndex((j) => j.id === row.id);
          if (idx >= 0) next[idx] = row;
          else next.unshift(row);
          if (payload.eventType === "UPDATE" && row.status === "ready") {
            toast.success(`Export ready: ${row.row_count ?? 0} rows`, {
              action: { label: "Download", onClick: () => void downloadJob(row) },
            });
          }
          return next.slice(0, 5);
        });
      })
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, []);

  const startAsyncExport = async (format: "csv" | "pdf") => {
    setStarting(true);
    try {
      const { data, error } = await supabase.functions.invoke("helpdesk-audit-export", {
        body: { format, filters: { tool, outcome } },
      });
      if (error) throw error;
      toast.info("Export queued — you'll be notified when it's ready");
      void logExportEvent("helpdesk_audit", format, { tool, outcome }, null, "async", (data as any)?.job_id);
      void loadJobs();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setStarting(false);
    }
  };

  const downloadJob = async (j: ExportJob) => {
    if (!j.storage_path) return;
    const { data, error } = await supabase.storage
      .from("helpdesk-exports")
      .createSignedUrl(j.storage_path, 60);
    if (error) return toast.error(error.message);
    window.open(data.signedUrl, "_blank");
  };


  const load = async () => {
    setLoading(true);
    let q = supabase
      .from("helpdesk_tool_audit")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    if (tool !== "all") q = q.eq("tool_name", tool);
    if (outcome !== "all") q = q.eq("outcome", outcome);
    const { data, error } = await q;
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setRows((data as AuditRow[]) ?? []);
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool, outcome]);

  const totals = rows.reduce(
    (acc, r) => {
      acc.n += 1;
      if (r.outcome === "ok") acc.ok += 1;
      if (r.outcome === "rate_limited") acc.rl += 1;
      if (r.outcome === "denied") acc.denied += 1;
      if (r.outcome === "error") acc.err += 1;
      if (r.tool_name === "create_ticket" && r.outcome === "ok") acc.tickets += 1;
      if (r.latency_ms != null) {
        acc.latSum += r.latency_ms;
        acc.latN += 1;
      }
      return acc;
    },
    { n: 0, ok: 0, rl: 0, denied: 0, err: 0, tickets: 0, latSum: 0, latN: 0 },
  );

  const avgLat = totals.latN ? Math.round(totals.latSum / totals.latN) : 0;

  return (
    <section className="border border-border rounded-2xl bg-card p-4 md:p-6 space-y-4">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Activity className="size-4 text-primary" />
          <div>
            <h3 className="font-display font-semibold text-base leading-tight">
              Helpdesk audit
            </h3>
            <p className="text-xs text-muted-foreground">
              Every AI tool call from the helpdesk chat (last 200 matching rows).
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={tool} onValueChange={(v) => setTool(v as any)}>
            <SelectTrigger className="w-[160px] h-9 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TOOLS.map((t) => (
                <SelectItem key={t} value={t} className="text-xs">
                  {t === "all" ? "All tools" : t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={outcome} onValueChange={(v) => setOutcome(v as any)}>
            <SelectTrigger className="w-[140px] h-9 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OUTCOMES.map((o) => (
                <SelectItem key={o} value={o} className="text-xs">
                  {o === "all" ? "All outcomes" : o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => {
            if (!rows.length) return toast.info("Nothing to export");
            download(`helpdesk-audit-${new Date().toISOString().slice(0,10)}.csv`, toCsv(rows), "text/csv");
            void logExportEvent("helpdesk_audit", "csv", { tool, outcome }, rows.length, "sync");
          }}>
            <Download className="size-3.5" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => {
            if (!rows.length) return toast.info("Nothing to export");
            openPdfPrint(rows, { tool, outcome });
            void logExportEvent("helpdesk_audit", "pdf", { tool, outcome }, rows.length, "sync");
          }}>
            <FileText className="size-3.5" /> PDF
          </Button>
          <Button variant="outline" size="sm" disabled={starting} onClick={() => void startAsyncExport("csv")}>
            <Sparkles className="size-3.5" /> Async CSV
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>

        </div>
      </header>

      {jobs.length > 0 && (
        <div className="border border-border rounded-lg p-3 space-y-2">
          <div className="text-[11px] font-mono uppercase text-muted-foreground">Background exports</div>
          {jobs.map((j) => (
            <div key={j.id} className="flex items-center gap-3 text-xs">
              <Badge variant="outline" className="text-[10px]">{j.format}</Badge>
              <Badge variant={j.status === "ready" ? "secondary" : j.status === "failed" ? "destructive" : "outline"} className="text-[10px]">
                {j.status}
              </Badge>
              <div className="flex-1 min-w-0">
                <Progress value={j.progress} className="h-1.5" />
              </div>
              <span className="text-muted-foreground tabular-nums">{j.row_count ?? 0} rows</span>
              {j.status === "ready" && j.storage_path && (
                <Button size="sm" variant="ghost" onClick={() => void downloadJob(j)}>
                  <Download className="size-3.5" /> Download
                </Button>
              )}
              {j.error && <span className="text-destructive truncate max-w-[200px]">{j.error}</span>}
            </div>
          ))}
        </div>
      )}


      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
        <Stat label="Rows" value={totals.n} />
        <Stat label="Tickets" value={totals.tickets} tone="primary" />
        <Stat label="Rate-limited" value={totals.rl} tone={totals.rl > 0 ? "warn" : undefined} />
        <Stat label="Denied" value={totals.denied} />
        <Stat label="Avg latency" value={`${avgLat}ms`} />
      </div>

      <div className="overflow-x-auto border border-border rounded-lg">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2 font-mono uppercase">When</th>
              <th className="text-left px-3 py-2 font-mono uppercase">Tool</th>
              <th className="text-left px-3 py-2 font-mono uppercase">Outcome</th>
              <th className="text-left px-3 py-2 font-mono uppercase">User / IP</th>
              <th className="text-left px-3 py-2 font-mono uppercase">Latency</th>
              <th className="text-left px-3 py-2 font-mono uppercase">Details</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border/60 align-top">
                <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                  {new Date(r.created_at).toLocaleString()}
                </td>
                <td className="px-3 py-2 font-mono">{r.tool_name}</td>
                <td className="px-3 py-2">
                  <Badge variant={outcomeVariant(r.outcome)} className="text-[10px]">
                    {r.outcome}
                  </Badge>
                </td>
                <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground">
                  {r.user_id ? r.user_id.slice(0, 8) : `ip:${(r.ip_hash ?? "").slice(0, 8)}`}
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {r.latency_ms != null ? `${r.latency_ms}ms` : "—"}
                </td>
                <td className="px-3 py-2 max-w-md">
                  {r.ticket_id && (
                    <div className="text-[11px]">
                      ticket: <span className="font-mono">{r.ticket_id.slice(0, 8)}</span>
                    </div>
                  )}
                  {r.error && (
                    <div className="text-[11px] text-destructive truncate">{r.error}</div>
                  )}
                  {r.input_summary && Object.keys(r.input_summary).length > 0 && (
                    <details className="text-[10px] text-muted-foreground">
                      <summary className="cursor-pointer">input</summary>
                      <pre className="whitespace-pre-wrap break-all">
                        {JSON.stringify(r.input_summary, null, 2)}
                      </pre>
                    </details>
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && !loading && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                  No audit rows for this filter yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone?: "primary" | "warn";
}) {
  const cls =
    tone === "primary"
      ? "text-primary"
      : tone === "warn"
        ? "text-amber-600 dark:text-amber-400"
        : "";
  return (
    <div className="border border-border/60 rounded-lg p-2">
      <div className="font-mono text-[10px] uppercase text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold ${cls}`}>{value}</div>
    </div>
  );
}
